// App.js — FULL app (centered auth, Clerk loading gate, protected shell)
import React, { useState, useEffect } from "react";
import { AssessmentProvider, useAssessment } from "./components/SharedContext";
import TabNavigation from "./components/TabNavigation";
import AIRubricPromptGenerator from "./components/AIRubricPromptGenerator";
import AssignmentPromptGenerator from "./components/AssignmentPromptGenerator";
import RubricCreator from "./components/RubricCreator";
import ClassListManager from "./components/ClassListManager";
import GradingTemplate from "./components/GradingTemplate";
import GradeBook from "./components/GradeBook";
import ExcelImportTest from "./components/ExcelImportTest";
import HelpPage from "./components/HelpPage";
import GradingPolicyManager from "./components/GradingPolicyManager";
import SchoolLogoSettings from "./components/SchoolLogoSettings";
import "./index.css";
import { ensureDefaultConestogaPoliciesLoaded } from "./services/policyBootstrap";

// Clerk + Router
import {
  SignedIn,
  SignedOut,
  SignIn,
  SignUp,
  RedirectToSignIn,
  UserButton,
  useUser,
  ClerkLoaded,
  ClerkLoading,
} from "@clerk/clerk-react";

// TanStack Query
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { queryClient } from "./lib/queryClient";

/* ---------- UI helpers ---------- */

const ScrollToTopButton = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => setIsVisible(window.pageYOffset > 200);
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          toggleVisibility();
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    setIsScrolling(true);
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      setTimeout(() => {
        if (window.pageYOffset > 50) {
          window.scrollTo(0, 0);
          document.body.scrollTop = 0;
          document.documentElement.scrollTop = 0;
        }
        setIsScrolling(false);
      }, 1500);
    } catch {
      window.scrollTo(0, 0);
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
      setIsScrolling(false);
    }
  };

  return (
    <>
      {isVisible && (
        <button
          onClick={scrollToTop}
          disabled={isScrolling}
          className={`fixed bottom-6 right-6 z-50 w-14 h-14 bg-gray-900 hover:bg-black text-white rounded-full shadow-2xl hover:shadow-3xl border-2 border-gray-700 hover:border-gray-600 transform transition-all duration-200 ${
            isScrolling ? "scale-95 opacity-75 cursor-wait" : "hover:scale-105 active:scale-95 cursor-pointer"
          } flex items-center justify-center backdrop-blur-sm ring-2 ring-white ring-opacity-20 hover:ring-opacity-40`}
          style={{
            background: isScrolling
              ? "linear-gradient(135deg, #374151 0%, #1f2937 100%)"
              : "linear-gradient(135deg, #1f2937 0%, #111827 100%)",
            boxShadow:
              "0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.2)",
          }}
          title="Scroll to top of page"
          aria-label="Scroll to top of page"
        >
          {isScrolling ? (
            <div className="relative">
              <svg className="w-6 h-6 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </div>
          ) : (
            <svg className="w-7 h-7 drop-shadow-sm" fill="currentColor" viewBox="0 0 24 24">
              <path
                fillRule="evenodd"
                d="M12 2.25a.75.75 0 01.75.75v16.19l6.22-6.22a.75.75 0 111.06 1.06l-7.5 7.5a.75.75 0 01-1.06 0l-7.5-7.5a.75.75 0 111.06-1.06l6.22 6.22V3a.75.75 0 01.75-.75z"
                clipRule="evenodd"
                transform="rotate(180 12 12)"
              />
            </svg>
          )}
          <div className="absolute inset-0 rounded-full bg-gray-900 opacity-20 animate-ping" />
        </button>
      )}
    </>
  );
};

/* ---------- App shell (protected) ---------- */
const AppShell = () => {
  const { activeTab } = useAssessment();
  const { user } = useUser();
  const [showPrivacyNotice, setShowPrivacyNotice] = useState(false);

  // Track which tabs have been visited so we can keep them mounted (hidden)
  // instead of unmounting them, which prevents data loss on tab switches.
  const [visitedTabs, setVisitedTabs] = useState(new Set());

  useEffect(() => {
    setVisitedTabs(prev => {
      if (prev.has(activeTab)) return prev;
      const next = new Set(prev);
      next.add(activeTab);
      return next;
    });
  }, [activeTab]);

  useEffect(() => {
    ensureDefaultConestogaPoliciesLoaded();
  }, []);

  useEffect(() => {
    const hasAcknowledged = sessionStorage.getItem(
      "gradingpilot.privacyNoticeAcknowledged"
    );
    if (!hasAcknowledged) {
      setShowPrivacyNotice(true);
    }
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      const warningMessage =
        "Reminder: export your grading session, gradebooks, and rubrics before leaving to avoid data loss.";
      event.preventDefault();
      event.returnValue = warningMessage;
      return warningMessage;
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  const acknowledgePrivacyNotice = () => {
    sessionStorage.setItem("gradingpilot.privacyNoticeAcknowledged", "true");
    setShowPrivacyNotice(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 relative">
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img
                src="./GradingPilot-Vector-Logo-v01.png"
                alt="GradingPilot Logo"
                className="h-20 w-20 object-contain"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">GradingPilot</h1>
                <p className="text-gray-600">Advanced grading and rubric creation tools for educators</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {user && (
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-800">Welcome, {user.fullName}</p>
                  <p className="text-xs text-gray-500">GradingPilot Professional Suite v2.0</p>
                </div>
              )}
              <UserButton afterSignOutUrl="https://www.gradingpilot.com" />
            </div>
          </div>
        </div>
      </div>

      <TabNavigation />

      <div className="tab-content">
        {/* Data-holding tabs: keep mounted once visited to prevent data loss.
            Uses CSS display:none so component state (including rich text editors,
            form inputs, rubric selections, and grading data) is fully preserved. */}
        {visitedTabs.has("grading-tool") && (
          <div style={{ display: activeTab === "grading-tool" ? undefined : "none" }}>
            <GradingTemplate />
          </div>
        )}
        {visitedTabs.has("rubric-creator") && (
          <div style={{ display: activeTab === "rubric-creator" ? undefined : "none" }}>
            <RubricCreator />
          </div>
        )}
        {visitedTabs.has("gradebook") && (
          <div style={{ display: activeTab === "gradebook" ? undefined : "none" }}>
            <GradeBook />
          </div>
        )}

        {/* Lightweight tabs: state lives in SharedContext, safe to unmount */}
        {activeTab === "ai-prompt-generator" && <AIRubricPromptGenerator />}
        {activeTab === "assignment-prompt-generator" && <AssignmentPromptGenerator />}
        {activeTab === "class-manager" && <ClassListManager />}
        {activeTab === "policy-manager" && <GradingPolicyManager />}
        {activeTab === "logo-settings" && <SchoolLogoSettings />}
        {activeTab === "excel-import-test" && <ExcelImportTest />}
        {activeTab === "help" && <HelpPage />}
      </div>

      <ScrollToTopButton />

      {showPrivacyNotice && (
        <div className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm flex items-center justify-center z-50 px-4">
          <div className="bg-white max-w-2xl w-full rounded-lg shadow-2xl p-6 border border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              Privacy Notice
            </h2>
            <div className="text-gray-700 space-y-3">
              <p>GradingPilot does not store any student information.</p>
              <p>
                All data entered into GradingPilot exists only for the current
                browser session and is automatically removed when the page is
                refreshed, closed, or exited. No student data, grades, rubrics,
                or planning materials are saved on our servers.
              </p>
              <p>
                If you wish to save or reuse any grading sessions, gradebooks,
                rubrics, or other planning materials, you must use the built-in
                export tools to download and store copies offline.
              </p>
              <p>
                This design is intentional and helps protect student privacy
                and confidential academic information. Please ensure you export
                your work before refreshing or leaving the application.
              </p>
            </div>
            <div className="mt-6 flex justify-end">
              <button
                onClick={acknowledgePrivacyNotice}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md shadow focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                I Understand
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ---------- Layouts & Guards ---------- */

// Center auth pages (use 100dvh for mobile browser chrome)
const AuthLayout = ({ children }) => (
  <div className="min-h-[100dvh] flex items-center justify-center bg-sky-50 p-6">
    <div className="w-full max-w-md">{children}</div>
  </div>
);

const normalizePath = () => {
  const cleaned = window.location.pathname.replace(/\/+$/, "").toLowerCase();
  return cleaned.startsWith("/app") ? cleaned.slice(4) || "/" : cleaned || "/";
};

const RequireAuth = ({ children }) => (
  <>
    <SignedIn>{children}</SignedIn>
    <SignedOut><RedirectToSignIn fallbackRedirectUrl="/app" /></SignedOut>
  </>
);

/* ---------- Routes ---------- */

const LoadingScreen = () => (
  <div className="min-h-[100dvh] grid place-items-center bg-sky-50">
    <div className="text-gray-700">Loading…</div>
  </div>
);

export default function App() {
  const path = normalizePath();
  const isSignIn = path === "/sign-in";
  const isSignUp = path === "/sign-up";

  return (
    <>
      <ClerkLoading><LoadingScreen /></ClerkLoading>
      <ClerkLoaded>
        {isSignIn && (
          <AuthLayout>
            <SignIn routing="virtual" />
          </AuthLayout>
        )}

        {isSignUp && (
          <AuthLayout>
            <SignUp routing="virtual" />
          </AuthLayout>
        )}

        {!isSignIn && !isSignUp && (
          <RequireAuth>
            <QueryClientProvider client={queryClient}>
              <AssessmentProvider>
                <AppShell />
              </AssessmentProvider>
              {process.env.NODE_ENV === "development" && (
                <ReactQueryDevtools
                  initialIsOpen={false}
                  position="bottom-left"
                  buttonPosition="bottom-left"
                />
              )}
            </QueryClientProvider>
          </RequireAuth>
        )}
      </ClerkLoaded>
    </>
  );
}